<template>
  <div class="media-library-page">
    <header class="page-header">
      <div>
        <h1>Media Library</h1>
        <p>Upload and manage images attached to content tiles. Copy an image URL to reuse it.</p>
      </div>
      <button class="primary-btn" @click="openCreate">
        <i class="bx bx-upload"></i>
        Upload Image
      </button>
    </header>

    <section class="summary-card">
      <div class="summary-icon"><i class="bx bx-images"></i></div>
      <div>
        <span>Images in library</span>
        <strong>{{ totalImages }}</strong>
      </div>
    </section>

    <section class="library-card">
      <div class="toolbar">
        <label class="search-box">
          <i class="bx bx-search"></i>
          <input
            v-model="search"
            type="search"
            placeholder="Search image or content title"
            @keyup.enter="loadImages"
          />
        </label>
        <button class="secondary-btn" :disabled="loading" @click="loadImages">
          <i class="bx bx-search"></i>
          Search
        </button>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Content tile</th>
              <th>Usage</th>
              <th>Image URL</th>
              <th class="actions-heading">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="5" class="empty-state">Loading images...</td>
            </tr>
            <tr v-else-if="images.length === 0">
              <td colspan="5" class="empty-state">
                <i class="bx bx-image"></i>
                <strong>No images found</strong>
                <span>Upload an image to a content tile to get started.</span>
              </td>
            </tr>
            <tr v-for="image in images" v-else :key="image.con_tile_id">
              <td>
                <img
                  class="thumbnail"
                  :src="getImageUrl(image.con_tile_img)"
                  :alt="image.con_tile_title"
                />
              </td>
              <td>
                <strong>{{ image.con_tile_title || "Untitled tile" }}</strong>
                <small>Tile #{{ image.con_tile_id }}</small>
              </td>
              <td><span class="usage-badge">Standalone image</span></td>
              <td class="url-cell" :title="image.con_tile_img">{{ image.con_tile_img }}</td>
              <td>
                <div class="actions">
                  <button class="action-btn copy" title="Copy image link" @click="copyLink(image.con_tile_img)">
                    <i class="bx bx-copy"></i><span>Copy link</span>
                  </button>
                  <button class="action-btn edit" title="Edit tile" @click="openEdit(image)">
                    <i class="bx bx-edit"></i>
                  </button>
                  <button class="action-btn remove" title="Remove image" @click="removeImage(image)">
                    <i class="bx bx-image-alt"></i>
                  </button>
                  <button class="action-btn delete" title="Delete content tile" @click="deleteTile(image)">
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer v-if="totalPages > 1" class="pagination">
        <button :disabled="page <= 1 || loading" @click="changePage(page - 1)">Previous</button>
        <span>Page {{ page }} of {{ totalPages }}</span>
        <button :disabled="page >= totalPages || loading" @click="changePage(page + 1)">Next</button>
      </footer>
    </section>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <section class="modal">
        <header class="modal-header">
          <div>
            <h2>{{ editingImage ? "Edit image" : "Upload image" }}</h2>
            <p>Upload an image and copy its public URL when it is ready.</p>
          </div>
          <button class="close-btn" aria-label="Close" @click="closeModal">
            <i class="bx bx-x"></i>
          </button>
        </header>

        <form @submit.prevent="saveImage">
          <label>
            Tile title <span class="required">*</span>
            <input v-model.trim="form.con_tile_title" type="text" required maxlength="255" />
          </label>

          <label>
            Image file <span v-if="!editingImage" class="required">*</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
              :required="!editingImage"
              @change="handleFileChange"
            />
            <small>JPG, PNG, GIF, WEBP, or AVIF; maximum 10 MB.</small>
          </label>

          <div v-if="previewUrl" class="preview-wrap">
            <img :src="previewUrl" alt="Selected image preview" />
          </div>

          <p v-if="formError" class="form-error">{{ formError }}</p>

          <footer class="modal-actions">
            <button type="button" class="secondary-btn" :disabled="saving" @click="closeModal">Cancel</button>
            <button type="submit" class="primary-btn" :disabled="saving">
              {{ saving ? "Saving..." : editingImage ? "Save changes" : "Upload image" }}
            </button>
          </footer>
        </form>
      </section>
    </div>
  </div>
</template>

<script>
import {
  createContentTileApi,
  deleteContentTileApi,
  getContentTilesApi,
  updateContentTileApi,
} from "@/apis/contenttileapis";
import { apiBaseUrl } from "@/apis/appconst";

export default {
  name: "MediaLibraryScreen",

  data() {
    return {
      loading: false,
      saving: false,
      images: [],
      totalImages: 0,
      page: 1,
      limit: 20,
      totalPages: 1,
      search: "",
      showModal: false,
      editingImage: null,
      selectedFile: null,
      previewUrl: "",
      formError: "",
      form: {
        con_tile_title: "",
      },
    };
  },

  mounted() {
    this.loadImages();
  },

  beforeUnmount() {
    this.clearPreviewUrl();
  },

  methods: {
    async loadImages() {
      this.loading = true;

      try {
        const response = await getContentTilesApi({
          page: this.page,
          limit: this.limit,
          search: this.search,
          imageOnly: true,
          mediaLibrary: true,
        });

        if (response?.status === false || response?.success === false) {
          throw new Error(response?.success || response?.message || "Unable to load images.");
        }

        const data = response?.data || response || {};
        this.images = Array.isArray(data.tiles) ? data.tiles : [];
        this.totalImages = Number(data.total || 0);
        this.totalPages = Math.max(Number(data.totalPages || 1), 1);
      } catch (error) {
        console.error("Media library load error:", error);
        window.alert(error.message || "Unable to load images.");
      } finally {
        this.loading = false;
      }
    },

    async changePage(page) {
      this.page = page;
      await this.loadImages();
    },

    openCreate() {
      this.editingImage = null;
      this.form = {
        con_tile_title: "",
      };
      this.selectedFile = null;
      this.formError = "";
      this.clearPreviewUrl();
      this.showModal = true;
    },

    openEdit(image) {
      this.editingImage = image;
      this.form = {
        con_tile_title: image.con_tile_title || "",
      };
      this.selectedFile = null;
      this.formError = "";
      this.clearPreviewUrl();
      this.previewUrl = this.getImageUrl(image.con_tile_img);
      this.showModal = true;
    },

    handleFileChange(event) {
      const file = event.target.files?.[0] || null;
      this.selectedFile = file;
      this.formError = "";
      this.clearPreviewUrl();

      if (!file) return;

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/avif",
      ];

      if (!allowedTypes.includes(file.type)) {
        this.selectedFile = null;
        this.formError = "Choose a JPG, PNG, GIF, WEBP, or AVIF image.";
        event.target.value = "";
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.selectedFile = null;
        this.formError = "Image files must be 10 MB or smaller.";
        event.target.value = "";
        return;
      }

      this.previewUrl = URL.createObjectURL(file);
    },

    async saveImage() {
      if (!this.form.con_tile_title) {
        this.formError = "Enter an image title.";
        return;
      }

      if (!this.editingImage && !this.selectedFile) {
        this.formError = "Choose an image to upload.";
        return;
      }

      this.saving = true;
      this.formError = "";

      const current = this.editingImage || {};
      const payload = {
        con_tile_id: current.con_tile_id || null,
        con_tile_parent: "",
        con_tile_icon: current.con_tile_icon || "",
        con_tile_title: this.form.con_tile_title,
        con_tile_meta_title: current.con_tile_meta_title || "",
        con_tile_keyword: current.con_tile_keyword || "",
        con_tile_desc: current.con_tile_desc || "",
        con_tile_img: current.con_tile_img || "",
        con_tile_download_url: current.con_tile_download_url || "",
        con_tile_cat: current.con_tile_cat ?? "",
        mediaLibrary: true,
      };

      if (this.selectedFile) payload.con_tile_img = this.selectedFile;

      try {
        const response = this.editingImage
          ? await updateContentTileApi(payload)
          : await createContentTileApi(payload);

        if (response?.status === true || response?.success === true) {
          this.closeModal();
          await this.loadImages();
          return;
        }

        throw new Error(response?.success || response?.message || "Unable to save image.");
      } catch (error) {
        console.error("Media library save error:", error);
        this.formError = error.message || "Unable to save image.";
      } finally {
        this.saving = false;
      }
    },

    async removeImage(image) {
      if (!window.confirm(`Remove the image from "${image.con_tile_title}"? The content tile will remain.`)) {
        return;
      }

      const payload = {
        con_tile_id: image.con_tile_id,
        con_tile_parent: "",
        con_tile_icon: image.con_tile_icon || "",
        con_tile_title: image.con_tile_title || "",
        con_tile_meta_title: image.con_tile_meta_title || "",
        con_tile_keyword: image.con_tile_keyword || "",
        con_tile_desc: image.con_tile_desc || "",
        con_tile_img: "",
        con_tile_download_url: image.con_tile_download_url || "",
        con_tile_cat: image.con_tile_cat ?? "",
        mediaLibrary: true,
      };

      try {
        const response = await updateContentTileApi(payload);
        if (response?.status !== true && response?.success !== true) {
          throw new Error(response?.success || response?.message || "Unable to remove image.");
        }
        await this.loadImages();
      } catch (error) {
        console.error("Media library image removal error:", error);
        window.alert(error.message || "Unable to remove image.");
      }
    },

    async deleteTile(image) {
      if (!window.confirm(`Delete the content tile "${image.con_tile_title}"? This also removes the tile from the website.`)) {
        return;
      }

      try {
        const response = await deleteContentTileApi(image.con_tile_id);
        if (response?.status !== true && response?.success !== true) {
          throw new Error(response?.success || response?.message || "Unable to delete content tile.");
        }
        await this.loadImages();
      } catch (error) {
        console.error("Media library tile deletion error:", error);
        window.alert(error.message || "Unable to delete content tile.");
      }
    },

    async copyLink(imageUrl) {
      try {
        await navigator.clipboard.writeText(this.getImageUrl(imageUrl));
        window.alert("Image link copied.");
      } catch (error) {
        console.error("Copy image link error:", error);
        window.alert("Unable to copy the image link. Check browser clipboard permissions.");
      }
    },

    getImageUrl(imageUrl) {
      if (!imageUrl) return "";
      try {
        return new URL(imageUrl, apiBaseUrl).toString();
      } catch (error) {
        console.error("Invalid image URL:", error);
        return imageUrl;
      }
    },

    clearPreviewUrl() {
      if (this.previewUrl.startsWith("blob:")) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = "";
    },

    closeModal() {
      this.showModal = false;
      this.clearPreviewUrl();
      this.selectedFile = null;
      this.formError = "";
    },
  },
};
</script>

<style scoped>
.media-library-page { padding: 28px; color: #172033; }
.page-header { display: flex; justify-content: space-between; align-items: center; gap: 20px; margin-bottom: 22px; }
.page-header h1 { margin: 0 0 6px; font-size: 26px; }
.page-header p { margin: 0; color: #64748b; }
.primary-btn, .secondary-btn, .pagination button { border: 0; border-radius: 8px; padding: 10px 15px; font: inherit; cursor: pointer; }
.primary-btn { color: #fff; background: #2563eb; }
.primary-btn:disabled, .secondary-btn:disabled, .pagination button:disabled { opacity: .55; cursor: not-allowed; }
.secondary-btn, .pagination button { color: #334155; background: #f1f5f9; }
.summary-card { display: flex; align-items: center; gap: 14px; width: fit-content; min-width: 220px; margin-bottom: 20px; padding: 16px 20px; border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; }
.summary-icon { display: grid; width: 42px; height: 42px; place-items: center; border-radius: 10px; color: #2563eb; background: #eff6ff; font-size: 22px; }
.summary-card span, .summary-card strong { display: block; }
.summary-card span { color: #64748b; font-size: 13px; }
.summary-card strong { margin-top: 3px; font-size: 22px; }
.library-card { overflow: hidden; border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; }
.toolbar { display: flex; gap: 10px; padding: 16px; border-bottom: 1px solid #e2e8f0; }
.search-box { display: flex; flex: 1; align-items: center; gap: 8px; max-width: 480px; padding: 0 12px; border: 1px solid #cbd5e1; border-radius: 8px; }
.search-box input { width: 100%; padding: 10px 0; border: 0; outline: 0; font: inherit; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; text-align: left; }
th, td { padding: 13px 15px; border-bottom: 1px solid #edf0f4; vertical-align: middle; }
th { color: #64748b; background: #f8fafc; font-size: 12px; text-transform: uppercase; }
td strong, td small { display: block; }
td small { margin-top: 4px; color: #64748b; }
.thumbnail { width: 64px; height: 52px; border-radius: 7px; object-fit: cover; background: #f1f5f9; }
.url-cell { max-width: 290px; overflow: hidden; color: #475569; text-overflow: ellipsis; white-space: nowrap; }
.usage-badge { display: inline-block; padding: 5px 8px; border-radius: 99px; color: #475569; background: #f1f5f9; font-size: 12px; }
.actions-heading { min-width: 220px; }
.actions { display: flex; align-items: center; gap: 6px; }
.action-btn { display: inline-flex; align-items: center; justify-content: center; gap: 5px; min-height: 34px; padding: 0 9px; border: 0; border-radius: 7px; cursor: pointer; }
.action-btn i { font-size: 17px; }
.action-btn.copy { color: #1d4ed8; background: #eff6ff; }
.action-btn.edit { color: #92400e; background: #fffbeb; }
.action-btn.remove { color: #475569; background: #f1f5f9; }
.action-btn.delete { color: #b91c1c; background: #fef2f2; }
.empty-state { height: 150px; color: #64748b; text-align: center; }
.empty-state i, .empty-state strong, .empty-state span { display: block; }
.empty-state i { margin-bottom: 8px; font-size: 24px; }
.empty-state strong { color: #334155; }
.empty-state span { margin-top: 5px; }
.pagination { display: flex; justify-content: flex-end; align-items: center; gap: 14px; padding: 14px 16px; color: #64748b; }
.modal-overlay { position: fixed; z-index: 1000; inset: 0; display: grid; place-items: center; padding: 20px; background: rgb(15 23 42 / 50%); }
.modal { width: min(100%, 520px); max-height: 90vh; overflow-y: auto; padding: 22px; border-radius: 14px; background: #fff; box-shadow: 0 20px 60px rgb(15 23 42 / 20%); }
.modal-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
.modal-header h2 { margin: 0 0 5px; }
.modal-header p { margin: 0; color: #64748b; font-size: 13px; }
.close-btn { border: 0; background: transparent; font-size: 24px; cursor: pointer; }
.modal form > label { display: block; margin-bottom: 16px; color: #334155; font-size: 14px; font-weight: 600; }
.modal input:not([type="file"]), .modal select { display: block; width: 100%; margin-top: 7px; padding: 10px 11px; border: 1px solid #cbd5e1; border-radius: 7px; font: inherit; }
.modal input[type="file"] { display: block; width: 100%; margin-top: 8px; font: inherit; }
.modal label small { display: block; margin-top: 6px; color: #64748b; font-weight: 400; }
.required, .form-error { color: #b91c1c; }
.preview-wrap { margin: 4px 0 16px; padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; text-align: center; }
.preview-wrap img { max-width: 100%; max-height: 220px; object-fit: contain; }
.form-error { margin: 0 0 12px; font-size: 13px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 9px; margin-top: 20px; }
@media (max-width: 700px) {
  .media-library-page { padding: 16px; }
  .page-header { align-items: flex-start; flex-direction: column; }
  .toolbar { flex-direction: column; }
  .search-box { max-width: none; }
  .actions { flex-wrap: wrap; }
}
</style>
